import React from 'react';
import Head from 'next/head';
import InvestorsHeader from '@components/investors/investor-header';
import { PageWrapper } from '@components/pagewrapper/pagewrapper';
import getSecFilings, { ISecFillingEntry } from '@anghami/neogateway/dist/endpoints/getSecFilings';
import getInvestorsLinks, {
	IGetInvestorsLinksResponse
} from '@anghami/neogateway/dist/endpoints/getInvestorsLinks';
import { initNeoGatewayServer } from '@helpers/neogateway';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import PressContainer from '@components/press/press-container';
import { i18n } from 'next-i18next';
import { NextSeo } from 'next-seo';
import metas from '@metas/investors.config';
import withAuth from '@components/auth';
import { statisResData } from '../../enums/staticRSS-constants';
import { GetServerSidePropsContext } from 'next';
import InvestorsAboutSection from '@components/investors/about-section';
import InvestorsHighlightsSection from '@components/investors/highlights-section';
import CorporateGovernanceSection from '@components/investors/corporate-governance-section';
import SecFilings from '@components/investors/investor-sec-filings';
import InvestorsResourcesSection from '@components/investors/resources-section';
import InvestorsFAQSection from '@components/investors/investors-faq-section';
import InvestorsNavTabs from '@components/investors/nav-tabs';

interface IProps {
	lang: string;
	contents: IInvestorsPageData;
}

interface IInvestorsPageData {
	sec_filings: ISecFillingEntry[];
	pdf_links: IGetInvestorsLinksResponse['links'];
}

function InvestorsPage({ lang, contents }: IProps) {
	const language = i18n.language || lang || 'en';
	const { pdf_links, sec_filings } = contents;

	return (
		<PageWrapper headerProps={{ shouldStartTransparent: true }}>
			<Head>
				<link rel="dns-prefetch" href="https://cdnweb.anghami.com" />
				<link
					rel="preload"
					href="https://cdnweb.anghami.com/tv/AdapterArabicDisplayWeb-Rg.woff2"
					as="font"
					type="font/woff2"
					crossOrigin="anonymous"
				/>
				<link
					rel="preload"
					href="https://cdnweb.anghami.com/tv/AdapterArabicDisplayWeb-Bd.woff2"
					as="font"
					type="font/woff2"
					crossOrigin="anonymous"
				/>
				<link
					rel="preload"
					href="https://cdnweb.anghami.com/new/fonts/anton/Anton-Regular.woff2"
					as="font"
					type="font/woff2"
					crossOrigin="anonymous"
				/>
			</Head>
			<NextSeo {...metas[language]} />
			<InvestorsHeader />
			<InvestorsNavTabs />
			<InvestorsAboutSection />
			<InvestorsHighlightsSection />
			<PressContainer count={6} type="investor" />
			<CorporateGovernanceSection pdfLinks={pdf_links} />
			<SecFilings contents={sec_filings} />
			<InvestorsFAQSection />
			<InvestorsResourcesSection />
		</PageWrapper>
	);
}

/**
 * Fetches investors page data from multiple endpoints
 * Uses Promise.allSettled to handle errors independently for each endpoint
 */
async function fetchInvestorsData(gateway: any): Promise<IInvestorsPageData> {
	const contents: IInvestorsPageData = {
		sec_filings: [],
		pdf_links: []
	};

	const [sec_filings_result, pdf_links_result] = await Promise.allSettled([
		gateway.callEndpoint(getSecFilings, {}, true),
		gateway.callEndpoint(getInvestorsLinks, {}, true)
	]);

	// Handle SEC filings response
	if (sec_filings_result.status === 'fulfilled') {
		const sec_filings = sec_filings_result.value;
		if (!sec_filings.data || sec_filings.data.error) {
			console.warn('SEC filings returned with error or empty data, using fallback');
			contents.sec_filings = statisResData;
		} else {
			contents.sec_filings = sec_filings.data.data;
		}
	} else {
		console.error('Failed to fetch SEC filings:', sec_filings_result.reason);
		contents.sec_filings = statisResData;
	}

	// Handle investor links response
	if (pdf_links_result.status === 'fulfilled') {
		const pdf_links = pdf_links_result.value;
		if (!pdf_links.data || pdf_links.data.error) {
			console.warn('Investor links returned with error or empty data');
			contents.pdf_links = [];
		} else {
			contents.pdf_links = pdf_links.data.links;
		}
	} else {
		console.error('Failed to fetch investor links:', pdf_links_result.reason);
		contents.pdf_links = [];
	}

	return contents;
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
	const { gateway, pageProps } = await initNeoGatewayServer(ctx);

	const contents = await fetchInvestorsData(gateway);

	return {
		props: {
			contents,
			lang: ctx.query?.lang,
			...pageProps,
			...(await serverSideTranslations(ctx.locale, ['common']))
		}
	};
}

export default withAuth(InvestorsPage);
